export type JurisdictionLevel = "city" | "county" | "state" | "utility";

export interface OnboardingDataPoint {
  key: string;
  label: string;
  jurisdiction: JurisdictionLevel;
  typical: boolean;
  visualPosition?: { x: number; y: number };
  industryIds?: string[];
  summary?: string;
  details?: string[];
  nonSpatial?: boolean;
}

export interface OnboardingStage {
  id: string;
  name: string;
  heading: string;
  subHeading?: string;
  dataPoints: OnboardingDataPoint[];
  subsections?: { label: string; keys: string[] }[];
}

const salesDataPoints: OnboardingDataPoint[] = [
  { key: "s-front-setback", label: "Front Setback (FSB)", jurisdiction: "city", typical: true, visualPosition: { x: 50, y: 92 }, summary: "25 ft from front property line" },
  { key: "s-roof-type", label: "Roof Type", jurisdiction: "city", typical: true, visualPosition: { x: 28, y: 18 }, summary: "Comp shingle, tile, or standing-seam metal" },
  { key: "s-num-layers", label: "Number of Roof Layers", jurisdiction: "city", typical: true, visualPosition: { x: 70, y: 18 }, summary: "Max 2 layers before tear-off required" },
  { key: "s-ridgeline-setback", label: "Ridgeline Setback", jurisdiction: "city", typical: true, visualPosition: { x: 50, y: 32 }, summary: "18 in from ridge for ventilation" },
  { key: "s-side-setback", label: "Side Setback", jurisdiction: "city", typical: true, visualPosition: { x: 92, y: 50 }, summary: "5 ft from side property line" },
  { key: "s-rear-setback", label: "Rear Setback", jurisdiction: "city", typical: true, visualPosition: { x: 18, y: 50 }, summary: "15 ft from rear property line" },
  { key: "s-max-system-size", label: "Max System Size (kW)", jurisdiction: "utility", typical: true, visualPosition: { x: 50, y: 60 }, summary: "10 kW AC residential cap" },
  { key: "s-solar-access-law", label: "Solar Access Law", jurisdiction: "state", typical: false, summary: "Protects panels from new shading by neighbors" },
  { key: "s-net-metering", label: "Net Metering Policy", jurisdiction: "utility", typical: false, summary: "1:1 retail credit, monthly true-up" },
  { key: "s-fire-setback", label: "Fire Setback", jurisdiction: "state", typical: false, summary: "3 ft from ridge and hips per IFC 1205" },
  { key: "s-historic-district", label: "Historic District Rules", jurisdiction: "city", typical: false, summary: "Architectural review required for street-facing arrays" },
  { key: "s-tree-ordinance", label: "Tree/Shade Ordinance", jurisdiction: "county", typical: false, summary: "Removal permits may apply for shade trees" },
];

const designDataPoints: OnboardingDataPoint[] = [
  { key: "d-fire-setback", label: "Fire Setbacks", jurisdiction: "state", typical: true, visualPosition: { x: 50, y: 88 }, summary: "3 feet" },
  { key: "d-prod-meter-required", label: "Production Meter Required", jurisdiction: "utility", typical: true, visualPosition: { x: 4, y: 30 }, summary: "Yes" },
  { key: "d-prod-meter-location", label: "Production Meter Location", jurisdiction: "utility", typical: true, visualPosition: { x: 4, y: 40 }, summary: "Within 10' and line of sight of utility meter" },
  { key: "d-ac-disconnect-required", label: "AC Disconnect Required", jurisdiction: "utility", typical: true, visualPosition: { x: 4, y: 55 }, summary: "Yes" },
  { key: "d-ac-disconnect-location", label: "AC Disconnect Location", jurisdiction: "utility", typical: true, visualPosition: { x: 4, y: 65 }, summary: "Within 10' and line of sight of utility meter" },
  { key: "d-energy-storage-location", label: "Energy Storage Location", jurisdiction: "state", typical: true, visualPosition: { x: 4, y: 80 }, summary: "External only" },
  { key: "d-max-system-size-dc", label: "Max System Size (DC)", jurisdiction: "utility", typical: true, visualPosition: { x: 40, y: 50 }, summary: "15 kW" },
  { key: "d-max-system-size-ac", label: "Max System Size (AC)", jurisdiction: "utility", typical: true, visualPosition: { x: 65, y: 50 }, summary: "10 kW" },
  {
    key: "d-site-plan-requirements",
    label: "Site Plan Requirements",
    jurisdiction: "city",
    typical: true,
    visualPosition: { x: 88, y: 88 },
    nonSpatial: true,
    summary: "Required elements for the site plan submittal",
    details: [
      "Size and location of new construction and existing structures",
      "Distances from all lot lines",
      "Established street grades and proposed finished grades",
      "Boundary line survey",
      "Location and size of photovoltaic panels/modules",
      "Location of solar array standoffs and spacing",
      "Conduit routes",
      "Locations of inverters, disconnect switches, combiner boxes",
      "Gates or access points with dimensions and plans",
      "Location of heat detectors for battery energy storage",
      "Size and location of battery storage equipment",
      "Electrical equipment related to battery storage (inverters, disconnects, conduits)",
    ],
  },
  { key: "d-production-meter-req", label: "Production Meter Requirements", jurisdiction: "utility", typical: true, visualPosition: { x: 96, y: 22 }, summary: "Systems over 10 kW DC; line side of inverter terminals" },
  { key: "d-ac-disconnect-req", label: "AC Disconnect Requirements", jurisdiction: "utility", typical: true, visualPosition: { x: 70, y: 70 }, summary: "Systems over 10 kW DC or supply-side connected" },
  { key: "d-tie-in-limitations", label: "Tie-in Limitations", jurisdiction: "utility", typical: true, visualPosition: { x: 96, y: 45 }, summary: "Service panel tie-in restrictions and rules" },
  { key: "d-grounding-req", label: "Grounding Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 96, y: 92 }, summary: "New Ground Rod" },
  {
    key: "d-line-diagram-req",
    label: "Line Diagram Requirements",
    jurisdiction: "city",
    typical: true,
    visualPosition: { x: 30, y: 92 },
    nonSpatial: true,
    summary: "Required elements on the single-line diagram submittal",
    details: [
      "Customer name",
      "Premise address",
      "City",
      "System Size (both AC and DC)",
      "Solar Panels: Number of PV Modules make & model",
      "Inverter: Make, Model, Amperage, Phase and Power Rating; UL 1741 Compliance",
      "Utility Accessible AC Disconnect: Lockable, with visible open; Labeled \"PV AC Disconnect\"; within 10' of billing meter",
      "Utility PV Production Meter: Amperage rating of customer provided meter socket",
      "Main Service Panel: Service panelboard voltage, amperage rating and main disconnect(s) size",
      "Utility Meter: Labeled \"Photovoltaic System Connected\"; service voltage, amperage",
    ],
  },
  { key: "d-wind-speed", label: "Wind Speed", jurisdiction: "state", typical: true, visualPosition: { x: 88, y: 12 }, summary: "95 mph" },
  { key: "d-ground-snow-load", label: "Ground Snow Load", jurisdiction: "state", typical: true, visualPosition: { x: 35, y: 28 }, summary: "20 psf" },
  { key: "d-pe-required", label: "Professional Engineering Required", jurisdiction: "city", typical: true, visualPosition: { x: 62, y: 50 }, summary: "Yes" },
  {
    key: "d-structural-plan-req",
    label: "Structural Plan Requirements",
    jurisdiction: "city",
    typical: true,
    visualPosition: { x: 15, y: 75 },
    nonSpatial: true,
    summary: "Required elements for the structural plan submittal",
    details: [
      "Completed Structural Criteria and supporting documentation",
      "Roof free from visible structural deficiencies or deflection",
      "Single roof covering installed",
      "Rail attachment points 1 foot from truss splices",
      "Anchor fastener data per manufacturer guidelines",
      "Roof framing plan: type, size, spacing of members",
      "Weight and support locations of solar panels",
      "Site-specific structural calculations",
      "Dimensioned full-height/width roof cross-sections",
      "Solar rack support locations and attachment methods",
    ],
  },
];

const approvalsDataPoints: OnboardingDataPoint[] = [
  { key: "a-ic-application", label: "Interconnection Application", jurisdiction: "utility", typical: true },
  { key: "a-ic-fee", label: "Interconnection Fee", jurisdiction: "utility", typical: true },
  { key: "a-ic-timeline", label: "Interconnection Timeline", jurisdiction: "utility", typical: true },
  { key: "a-ic-insurance", label: "Insurance Requirements", jurisdiction: "utility", typical: true },
  { key: "a-ic-inspection", label: "Utility Inspection Required", jurisdiction: "utility", typical: true },
  { key: "a-ic-net-meter-app", label: "Net Metering Application", jurisdiction: "utility", typical: true },
  { key: "a-pm-fee", label: "Permit Fee", jurisdiction: "city", typical: true },
  { key: "a-pm-timeline", label: "Review Timeline", jurisdiction: "city", typical: true },
  { key: "a-pm-expedited", label: "Expedited Permitting", jurisdiction: "city", typical: true },
  { key: "a-pm-plan-sets", label: "Plan Set Requirements", jurisdiction: "city", typical: true },
  { key: "a-pm-inspection", label: "Inspection Requirements", jurisdiction: "city", typical: true },
  { key: "a-pm-ahj-contact", label: "AHJ Contact Info", jurisdiction: "city", typical: true },
  { key: "a-pm-structural-stamp", label: "Structural Stamp Required", jurisdiction: "city", typical: true },
  { key: "a-pm-electrical-stamp", label: "Electrical Stamp Required", jurisdiction: "city", typical: true },
];

export const ONBOARDING_STAGES: OnboardingStage[] = [
  {
    id: "sales",
    name: "Sales",
    heading: "Here's the regulatory data solar teams typically pull during sales.",
    subHeading: "Adjust the list to match your process — we'll use it to build your dataset.",
    dataPoints: salesDataPoints,
  },
  {
    id: "design",
    name: "Design & Engineering",
    heading: "Now add the data your team needs at design & engineering.",
    subHeading: "We've pre-selected the typical points. Expand Electrical or Structural to refine — this becomes part of your dataset.",
    dataPoints: designDataPoints,
    subsections: [
      { label: "Electrical", keys: ["d-production-meter-req", "d-ac-disconnect-req", "d-tie-in-limitations", "d-grounding-req", "d-line-diagram-req"] },
      { label: "Structural", keys: ["d-wind-speed", "d-ground-snow-load", "d-pe-required", "d-structural-plan-req"] },
    ],
  },
  {
    id: "approvals",
    name: "Approvals",
    heading: "We automatically provide the processes and requirements for Interconnection and Permitting",
    subHeading: "So you have them at your fingertips — no digging needed.",
    dataPoints: approvalsDataPoints,
    subsections: [
      { label: "Interconnection", keys: ["a-ic-application", "a-ic-fee", "a-ic-timeline", "a-ic-insurance", "a-ic-inspection", "a-ic-net-meter-app"] },
      { label: "Permitting", keys: ["a-pm-fee", "a-pm-timeline", "a-pm-expedited", "a-pm-plan-sets", "a-pm-inspection", "a-pm-ahj-contact", "a-pm-structural-stamp", "a-pm-electrical-stamp"] },
    ],
  },
];

export const ALL_ONBOARDING_DATA_POINTS = ONBOARDING_STAGES
  .flatMap(s => s.dataPoints)
  .map(dp => ({ ...dp, industryIds: dp.industryIds ?? ["solar-storage"] }));
